import React, { useState } from "react";
import ToolsCard from "../../misc/ToolsCard";
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from "react-redux";
import { CompareOneRosterUserEntraId, CoreOneRosterUser } from "../../../models/CoreOneRosterModels";
import { RequestState } from "../../../models/RequestState";
import { RootState } from "../../../store/store";
import { GetOneRosterUserCompareFromEntra, GetOneRosterUserInfoFromEntra } from "../../../store/OneRoster/OneRosterUserAction";
import StatusCode from "../../../util/StatusCode";
import ModalComponent from "../../misc/ModalComponent";
import * as Helper from '../../../util/Helper';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Loading from "../../misc/Loading";
import TableHorizontal, { InfoObject } from "../../misc/TableHorizontal";


enum InfoType {
    UNDEFINED = 'UNDEFINED',
    ENTRAID = 'ENTRAID',
    ENTRAID_COMPARE = 'ENTRAID_COMPARE'
}

const OneRosterUserTools = () => {
    const { t } = useTranslation(['common', 'oneRoster']);
    const dispatch = useDispatch();

    const [openModal, setOpenModal] = useState<boolean>(false);
    const [info, setInfo] = useState<InfoType>(InfoType.UNDEFINED);

    const user = useSelector<RootState, RequestState<CoreOneRosterUser>>(state => state.onerosterUser.user);
    const entraIdUserInfo = useSelector<RootState, RequestState<string>>(state => state.onerosterUser.infoFromEntraId);
    const compareOneRosterUser = useSelector<RootState, RequestState<CompareOneRosterUserEntraId>>(state => state.onerosterUser.compareOneRosterUser);

    const getMicrosoftEntraId = () => {
        setOpenModal(true);
        setInfo(InfoType.ENTRAID);
        if (user.code === StatusCode.COMPLETE && user.data) {
            GetOneRosterUserInfoFromEntra(user.data.id)(dispatch);
        }
    }

    const checkOneRoster = () => {
        setOpenModal(true);
        setInfo(InfoType.ENTRAID_COMPARE);
        if (user.code === StatusCode.COMPLETE && user.data) {
            GetOneRosterUserCompareFromEntra(user.data.id)(dispatch);
        }
    }

    const getIcon = (value: boolean) => {
        if (value === true) {
            return <FontAwesomeIcon color="green" icon="check-circle" className="icon" />
        }
        if (value === false) {
            return <FontAwesomeIcon color="red" icon="times-circle" className="icon" />
        }
    }

    const mapConflictingUsers = (): InfoObject[] => {
        if (compareOneRosterUser.code === StatusCode.COMPLETE) {
            if (compareOneRosterUser.data.conflictingUsers === undefined) {
                return [];
            }

            return compareOneRosterUser.data.conflictingUsers?.map(user => ({
                id: user.id,
                data: [
                    { value: user.displayName, className: user.actual ? 'actual' : undefined },
                    { value: user.userPrincipalName, className: user.actual ? 'actual' : undefined },
                    { value: user.actual && <span title={t("tools.user.ENTRAID_COMPARE.conflictingUsers.actualTitle", { ns: 'oneRoster' })}><FontAwesomeIcon color="green" icon="check-circle" className="icon" /></span>, className: user.actual ? 'actual' : undefined }
                ]
            }))
        } else {
            return [];
        }
    };

    const renderBody = () => {
        switch (info) {
            case InfoType.ENTRAID:
                if (entraIdUserInfo.code === StatusCode.PENDING) {
                    return <Loading />;
                };
                if (entraIdUserInfo.code === StatusCode.COMPLETE) {
                    return <pre>{Helper.formatText(entraIdUserInfo.data)}</pre>
                };
                if (entraIdUserInfo.code === StatusCode.ERROR) {
                    if (entraIdUserInfo.error.response?.status === 502) {
                        return t("tools.user.couldNotFetch", { ns: 'oneRoster' });
                    }
                    if (entraIdUserInfo.error.response?.status === 404) {
                        return t("tools.user.noUserFound", { ns: 'oneRoster' });
                    }
                    return t('error.somethingWentWrong', { ns: 'common' })
                };
                break;
            case InfoType.ENTRAID_COMPARE:
                if (compareOneRosterUser.code === StatusCode.PENDING) {
                    return <Loading />;
                };
                if (compareOneRosterUser.code === StatusCode.COMPLETE) {
                    const hasConflictingUsers = !compareOneRosterUser.data.noConflict;
                    return (<div style={{ marginBottom: '20px' }}>
                        <pre>{user.code === StatusCode.COMPLETE && `SIS ID: ${user?.data.id}`}</pre>
                        <div className="card-body-compare">
                            <span>{t("tools.user.ENTRAID_COMPARE.oneRosterEqualsEntra", { ns: 'oneRoster' })} {getIcon(compareOneRosterUser.data.sameSisId)}</span>
                            <span>{t("tools.user.ENTRAID_COMPARE.reuseSisId", { ns: 'oneRoster' })} {getIcon(compareOneRosterUser.data.noConflict)}</span>
                        </div>
                        {hasConflictingUsers && <>
                            <p style={{ padding: '10px 10px 0 10px' }}>{t('tools.user.ENTRAID_COMPARE.matchingUsers', { ns: 'oneRoster' })}</p>
                            <TableHorizontal
                                header={[
                                    { value: t("tools.user.ENTRAID_COMPARE.conflictingUsers.name", { ns: 'oneRoster' }) },
                                    { value: t("tools.user.ENTRAID_COMPARE.conflictingUsers.upn", { ns: 'oneRoster' }) },
                                    { value: "" }
                                ]}
                                info={mapConflictingUsers()}
                                searching={false}
                                error={false}
                            />
                        </>}
                    </div>);
                };
                if (compareOneRosterUser.code === StatusCode.ERROR) {
                    if (compareOneRosterUser.error.response?.status === 502) {
                        return t("tools.user.couldNotFetch", { ns: 'oneRoster' });
                    }
                    if (compareOneRosterUser.error.response?.status === 404) {
                        return t("tools.user.noUserFound", { ns: 'oneRoster' });
                    }
                    return t('error.somethingWentWrong', { ns: 'common' })
                };
                break;
            case InfoType.UNDEFINED: return null;
            default: return null;
        }
    }

    const showInformation = () => (
        <ModalComponent
            header={t(`tools.user.${info}.title`, { ns: 'oneRoster' })}
            cancel={t('action.close', { ns: 'common' })}
            isOpen={openModal}
            size="lg"
            toggleModal={() => (setOpenModal(false), setInfo(InfoType.UNDEFINED))}
        >
            {renderBody()}
        </ModalComponent >
    );

    return <div className="tab-border">
        <h5 className="pt-4">{t('tools.title', { ns: 'oneRoster' })}</h5>
        <p>{t('tools.description', { ns: 'oneRoster' })}</p>
        <div className="toolscard-display">
            <ToolsCard
                title={t(`tools.user.${InfoType.ENTRAID}.title`, { ns: 'oneRoster' })}
                description={t(`tools.user.${InfoType.ENTRAID}.description`, { ns: 'oneRoster' })}
                onChange={() => getMicrosoftEntraId()}
            />
            <ToolsCard
                title={t(`tools.user.${InfoType.ENTRAID_COMPARE}.title`, { ns: 'oneRoster' })}
                description={t(`tools.user.${InfoType.ENTRAID_COMPARE}.description`, { ns: 'oneRoster' })}
                onChange={() => (checkOneRoster())}
            />
        </div>
        {openModal && showInformation()}
    </div>
}

export default OneRosterUserTools;
